import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Autocomplete, TextField, MenuItem, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useLoader from "../../../hooks/useLoader.js";
import useModal from "../../../hooks/useModal.js";
import { formatDateToYYYYMMDD } from "../../../Helper/functionHelper.js";
import { getEmployees } from "../../../services/employee.js";
import { createWageRate, updateWageRate } from "../../../services/wageRate.js";
import { getActivePayrollTemplates } from "../../../services/payrollTemplate.js";
import PayheadRowsEditor from "../shared/PayheadRowsEditor.jsx";
import useActiveSalaryCategories from "../shared/useActiveSalaryCategories.js";
import {
  toPayheadPayload,
  computePayheadsError,
  defaultPfRule,
  toPfRulePayload,
  validatePfRuleFields,
  describePfRule,
} from "../shared/payheadUtils.js";

const PAY_BASIS_OPTIONS = [
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Bi-weekly", value: "BIWEEKLY" },
  { label: "Hourly", value: "HOURLY" },
];

const STATUS_OPTIONS = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];

const isBlank = (val) => val === "" || val === null || val === undefined;

const employeeLabel = (emp) =>
  emp ? `${[emp._FirstName, emp._MiddleName, emp._LastName].filter(Boolean).join(" ")} (${emp._UserEmployeeID})` : "";

const AddEditWageRate = ({ refresh, existingField = null }) => {
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();
  const isEdit = Boolean(existingField);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    pay_basis: existingField?.pay_basis || "DAILY",
    rate_amount: existingField?.rate_amount ?? "",
    standard_hours_per_day: existingField?.standard_hours_per_day ?? "",
    working_days_per_week: existingField?.working_days_per_week ?? "",
    week_off_paid: existingField?.week_off_paid ?? true,
    leave_paid: existingField?.leave_paid ?? true,
    holiday_paid: existingField?.holiday_paid ?? true,
    lwp_paid: existingField?.lwp_paid ?? false,
    effective_from: existingField?.effective_from ? new Date(existingField.effective_from) : null,
    effective_to: existingField?.effective_to ? new Date(existingField.effective_to) : null,
    status: existingField?.status ?? 1,
    template_id: existingField?.template_id ?? "",
  });
  const [components, setComponents] = useState(existingField?.components || []);
  const [pfRule, setPfRule] = useState(defaultPfRule(existingField || {}));
  const categories = useActiveSalaryCategories();
  const [templates, setTemplates] = useState([]);
  // Inline errors are derived from the CURRENT values (see `errors` below)
  // once a Submit has been attempted, instead of being stored - a stored
  // object is only rewritten on Submit, so fixing a field left its old
  // message on screen.
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Recomputed on every render (not just on Submit), so the error
  // appears/updates/clears live as the admin edits a value or Rate
  // Amount - it was previously only set inside validate() and left
  // stale until the next Submit click. componentsError (used to block
  // Submit) includes the Rate-Amount mismatch; componentsDisplayError
  // (passed down for display) omits it since PayheadRowsEditor already
  // shows that exact comparison itself, live, in its own summary line.
  const componentsError = useMemo(
    () => computePayheadsError(components, formData.rate_amount, categories),
    [components, formData.rate_amount, categories]
  );
  const componentsDisplayError = useMemo(
    () => computePayheadsError(components, formData.rate_amount, categories, { includeMismatch: false }),
    [components, formData.rate_amount, categories]
  );
  const pfRuleError = useMemo(() => validatePfRuleFields(pfRule, components), [pfRule, components]);
  // A wage rate never authors its own PF Rule - it only ever inherits one
  // by copying a template (see applyTemplate below), so there's nothing
  // to show unless a template is actually selected right now.
  const pfRuleActive = Boolean(formData.template_id) && pfRule.pf_rule_type !== "NONE";
  const pfRuleDescription = useMemo(() => (pfRuleActive ? describePfRule(pfRule, categories) : null), [pfRuleActive, pfRule, categories]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await getEmployees();
        const list = response?.data?.data || [];
        setEmployees(list);

        if (isEdit && existingField?.employee_id) {
          const match = list.find((e) => e._UserID === existingField.employee_id);
          if (match) setSelectedEmployee(match);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Failed to load employees");
      }
    };
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await getActivePayrollTemplates(formData.pay_basis);
        setTemplates(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching payroll templates:", error);
      }
    };
    if (formData.pay_basis) fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pay_basis]);

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  // Applying a template COPIES its payhead rows in as this wage rate's own,
  // independently-editable components - same as CI4 copying a template
  // into an employee's payroll structure. The PF Rule is different: it's
  // copied in too, but stays read-only here (see pfRuleActive/
  // pfRuleDescription above) - a wage rate only ever inherits a PF Rule
  // wholesale from whichever template is selected, it's never authored by
  // hand on this screen. Switching template later never reaches back and
  // changes what was already copied in. Rate Amount is untouched - it's
  // always a direct input, the template only supplies the breakdown.
  const applyTemplate = (templateId) => {
    setField("template_id", templateId);

    if (!templateId) {
      // Switching back to "None" means starting the breakdown over -
      // whatever was copied in (or built manually) no longer applies.
      setComponents([]);
      setPfRule(defaultPfRule());
      return;
    }

    const template = templates.find((t) => String(t.id) === String(templateId));
    if (template) {
      setComponents(template.payheads.map((p) => ({ ...p })));
      setPfRule(defaultPfRule(template));
    }
  };

  const computeErrors = () => {
    const nextErrors = {};

    if (!isEdit && !selectedEmployee) nextErrors.employee_id = "Employee is required";
    if (!formData.pay_basis) nextErrors.pay_basis = "Pay basis is required";

    if (isBlank(formData.rate_amount)) {
      nextErrors.rate_amount = "Rate is required";
    } else if (Number.isNaN(Number(formData.rate_amount)) || Number(formData.rate_amount) <= 0) {
      nextErrors.rate_amount = "Rate must be greater than 0";
    }

    if (componentsError) {
      nextErrors.components = componentsError;
    }

    if (pfRuleError) {
      nextErrors.pfRule = pfRuleError;
    }

    if (formData.pay_basis === "HOURLY") {
      if (isBlank(formData.standard_hours_per_day)) {
        nextErrors.standard_hours_per_day = "Required for hourly pay basis";
      } else if (Number.isNaN(Number(formData.standard_hours_per_day)) || Number(formData.standard_hours_per_day) <= 0) {
        nextErrors.standard_hours_per_day = "Must be greater than 0";
      }
    }

    if (formData.pay_basis === "WEEKLY" || formData.pay_basis === "BIWEEKLY") {
      if (isBlank(formData.working_days_per_week)) {
        nextErrors.working_days_per_week = "Required for weekly / bi-weekly pay basis";
      } else if (
        Number.isNaN(Number(formData.working_days_per_week)) ||
        !Number.isInteger(Number(formData.working_days_per_week)) ||
        Number(formData.working_days_per_week) < 1 ||
        Number(formData.working_days_per_week) > 7
      ) {
        nextErrors.working_days_per_week = "Must be a whole number between 1 and 7";
      }
    }

    const isInvalidDate = (d) => d instanceof Date && Number.isNaN(d.getTime());
    if (!formData.effective_from) nextErrors.effective_from = "Effective from is required";
    else if (isInvalidDate(formData.effective_from)) nextErrors.effective_from = "Effective from must be a valid date";

    if (isInvalidDate(formData.effective_to)) {
      nextErrors.effective_to = "Effective to must be a valid date";
    } else if (
      formData.effective_from &&
      !isInvalidDate(formData.effective_from) &&
      formData.effective_to &&
      formData.effective_to < formData.effective_from
    ) {
      nextErrors.effective_to = "Must be on or after effective from";
    }

    return nextErrors;
  };

  const errors = submitted ? computeErrors() : {};

  const validate = () => {
    setSubmitted(true);
    return Object.keys(computeErrors()).length === 0;
  };

  const buildPayload = () => ({
    employee_id: isEdit ? existingField.employee_id : selectedEmployee?._UserID,
    pay_basis: formData.pay_basis,
    rate_amount: Number(formData.rate_amount),
    standard_hours_per_day: isBlank(formData.standard_hours_per_day) ? null : Number(formData.standard_hours_per_day),
    working_days_per_week: isBlank(formData.working_days_per_week) ? null : Number(formData.working_days_per_week),
    week_off_paid: Boolean(formData.week_off_paid),
    leave_paid: Boolean(formData.leave_paid),
    holiday_paid: Boolean(formData.holiday_paid),
    lwp_paid: Boolean(formData.lwp_paid),
    effective_from: formatDateToYYYYMMDD(formData.effective_from),
    effective_to: formData.effective_to ? formatDateToYYYYMMDD(formData.effective_to) : null,
    status: Number(formData.status),
    template_id: formData.template_id || null,
    components: toPayheadPayload(components),
    ...toPfRulePayload(pfRule),
  });

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    showLoader();

    try {
      const payload = buildPayload();
      const response = isEdit ? await updateWageRate(existingField.id, payload) : await createWageRate(payload);
      toast.success(response?.data?.message || (isEdit ? "Wage rate updated successfully!" : "Wage rate added successfully!"));
      refresh?.();
      hideModal?.();
    } catch (error) {
      console.error("Error saving wage rate:", error);
      toast.error(error?.response?.data?.message || "Failed to save wage rate");
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Employee <span className="text-danger">*</span>
          </label>
          {isEdit ? (
            <div className="form-control form-control-sm bg-light" style={{ pointerEvents: "none" }}>
              {existingField.employee_name} {existingField.employee_code ? `(${existingField.employee_code})` : ""}
            </div>
          ) : (
            <Autocomplete
              size="small"
              options={employees}
              getOptionLabel={employeeLabel}
              isOptionEqualToValue={(opt, val) => opt._UserID === val._UserID}
              value={selectedEmployee}
              onChange={(e, value) => setSelectedEmployee(value)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" error={!!errors.employee_id} helperText={errors.employee_id} />
              )}
            />
          )}
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Pay Basis <span className="text-danger">*</span>
          </label>
          <TextField
            select
            size="small"
            fullWidth
            value={formData.pay_basis}
            onChange={(e) => setField("pay_basis", e.target.value)}
            error={!!errors.pay_basis}
            helperText={errors.pay_basis}
          >
            {PAY_BASIS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Rate Amount (₹) <span className="text-danger">*</span>
          </label>
          <TextField
            size="small"
            fullWidth
            type="number"
            slotProps={{ htmlInput: { min: 0 } }}
            value={formData.rate_amount}
            onChange={(e) => setField("rate_amount", e.target.value)}
            error={!!errors.rate_amount}
            helperText={errors.rate_amount}
          />
        </div>

        {formData.pay_basis === "HOURLY" && (
          <div className="col-md-6 mb-3">
            <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
              Standard Hours / Day <span className="text-danger">*</span>
            </label>
            <TextField
              size="small"
              fullWidth
              type="number"
              slotProps={{ htmlInput: { min: 0 } }}
              value={formData.standard_hours_per_day}
              onChange={(e) => setField("standard_hours_per_day", e.target.value)}
              error={!!errors.standard_hours_per_day}
              helperText={errors.standard_hours_per_day}
            />
          </div>
        )}

        {(formData.pay_basis === "WEEKLY" || formData.pay_basis === "BIWEEKLY") && (
          <div className="col-md-6 mb-3">
            <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
              Working Days / Week <span className="text-danger">*</span>
            </label>
            <TextField
              size="small"
              fullWidth
              type="number"
              slotProps={{ htmlInput: { min: 1, max: 7 } }}
              value={formData.working_days_per_week}
              onChange={(e) => setField("working_days_per_week", e.target.value)}
              error={!!errors.working_days_per_week}
              helperText={errors.working_days_per_week}
            />
          </div>
        )}

        <div className="col-md-6 mb-3">
          <label className="form-label d-flex align-items-center gap-1" style={{ fontWeight: 600, fontSize: "12px" }}>
            Payroll Template
            <Tooltip title="Pick a reusable template to copy its Basic/HRA/DA/TA structure in below - you can still edit it afterward. Purely a shortcut; you can build components from scratch instead.">
              <InfoOutlined sx={{ fontSize: 14, color: "#888" }} />
            </Tooltip>
          </label>
          <TextField select size="small" fullWidth value={formData.template_id} onChange={(e) => applyTemplate(e.target.value)}>
            <MenuItem value="">— None (build manually) —</MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <PayheadRowsEditor
          rows={components}
          onChange={setComponents}
          categories={categories}
          baseRateAmount={Number(formData.rate_amount) || 0}
          error={componentsDisplayError}
          pfRule={pfRule}
          onPfRuleChange={(patch) => setPfRule((prev) => ({ ...prev, ...patch }))}
        />

        {/* Which row is "PF" is always the admin's own choice (the "PF"
            checkbox above, same as Basic) - a template only supplies the
            RULE itself (the percentages/threshold), never which row it
            applies to. With no template selected, a PF-flagged row is
            simply calculated like any other payhead, from its own Method/
            Value/Based On (including its own PF Wage Base, editable right
            there in the grid) - same as manual wage-rate configuration
            worked before this feature existed. */}
        {pfRuleActive && (
          <div className="col-12 mb-3">
            <div className="alert alert-info py-2 px-3 mb-0" style={{ fontSize: "12px" }}>
              <strong>PF Rule (from template):</strong> {pfRuleDescription} - applies to whichever row above is checked "PF".
              <div className="text-muted">Edit the payroll template to change the rule itself, or the PF Wage Base chips on that row above.</div>
              {pfRuleError && <div className="text-danger mt-1">{pfRuleError}</div>}
            </div>
          </div>
        )}

        <div className="col-12 mb-3">
          <label className="form-label d-flex align-items-center gap-1" style={{ fontWeight: 600, fontSize: "12px" }}>
            Paid Non-Working Days
            <Tooltip title="When checked, that category is paid at this rate - a full day for DAILY/WEEKLY/BIWEEKLY, or Standard Hours/Day worth of hours for HOURLY. Week-off, Holiday and Leave default to paid; LWP defaults to unpaid (it means Loss of Pay).">
              <InfoOutlined sx={{ fontSize: 14, color: "#888" }} />
            </Tooltip>
          </label>
          <div className="d-flex flex-wrap gap-3">
            <FormControlLabel
              control={<Checkbox size="small" checked={formData.week_off_paid} onChange={(e) => setField("week_off_paid", e.target.checked)} />}
              label="Week-off"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={formData.holiday_paid} onChange={(e) => setField("holiday_paid", e.target.checked)} />}
              label="Holiday"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={formData.leave_paid} onChange={(e) => setField("leave_paid", e.target.checked)} />}
              label="Leave"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={formData.lwp_paid} onChange={(e) => setField("lwp_paid", e.target.checked)} />}
              label="LWP"
            />
          </div>
          {formData.pay_basis === "HOURLY" &&
            (formData.week_off_paid || formData.holiday_paid || formData.leave_paid || formData.lwp_paid) &&
            isBlank(formData.standard_hours_per_day) && (
              <div className="form-text text-danger">Set Standard Hours / Day above - it's used to credit hours for these paid days.</div>
            )}
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
            Effective From <span className="text-danger">*</span>
          </label>
          <DatePicker
            format="dd-MMM-yyyy"
            value={formData.effective_from}
            onChange={(value) => setField("effective_from", value)}
            slotProps={{
              field: { clearable: true },
              textField: {
                size: "small",
                fullWidth: true,
                error: !!errors.effective_from,
                helperText: errors.effective_from,
              },
            }}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
            Effective To
          </label>
          <DatePicker
            format="dd-MMM-yyyy"
            value={formData.effective_to}
            onChange={(value) => setField("effective_to", value)}
            minDate={formData.effective_from || undefined}
            slotProps={{
              field: { clearable: true },
              textField: {
                size: "small",
                fullWidth: true,
                error: !!errors.effective_to,
                helperText: errors.effective_to || "Leave blank for open-ended",
              },
            }}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Status
          </label>
          <TextField select size="small" fullWidth value={formData.status} onChange={(e) => setField("status", Number(e.target.value))}>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2" style={{ borderTop: "1px solid #e0e0e0", paddingTop: "12px" }}>
        <button type="button" onClick={hideModal} className="btn btn-secondary btn-sm" disabled={isSubmitting}>
          Cancel
        </button>
        <button type="button" className="btn btn-success btn-sm" disabled={isSubmitting} onClick={handleSubmit}>
          {isSubmitting ? "Saving..." : isEdit ? "Update" : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default AddEditWageRate;
